import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  CloudUpload as DataImportIcon,
  Analytics as AnalyticsIcon,
  Assessment as ReportsIcon,
  Info as DataStatusIcon,
  Settings as SettingsIcon,
  CheckCircle as AttendanceIcon,
  Cable as IntegrationIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Psychology as MLIcon,
  TrendingUp as LearningIcon,
  Sync as SyncIcon,
  Build as IndicatorIcon,
  BarChart as VisualizationIcon,
  VerifiedUser as ValidationIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout, fetchUserProfile } from '../../store/slices/authSlice';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import NotificationCenter from './NotificationCenter';

const drawerWidth = 240;

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const menuItems: MenuItemType[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Visualization & Analysis', icon: <VisualizationIcon />, path: '/visualization' },
  { text: 'Indicators & Mapping', icon: <IndicatorIcon />, path: '/indicators' },
  { text: 'School Map Report', icon: <MapIcon />, path: '/school-map' },
  { text: 'Schools', icon: <SchoolIcon />, path: '/schools' },
  { text: 'Attendance', icon: <AttendanceIcon />, path: '/attendance' },
  { text: 'ML Analytics', icon: <MLIcon />, path: '/ml-analytics/dropout-risk' },
  { text: 'Learning Analytics', icon: <LearningIcon />, path: '/ml-analytics/learning' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { text: 'Data Import', icon: <DataImportIcon />, path: '/data-import' },
  { text: 'Data Sync', icon: <SyncIcon />, path: '/data-sync' },
  { text: 'Data Quality', icon: <ValidationIcon />, path: '/data-quality/advanced' },
  { text: 'Integrations', icon: <IntegrationIcon />, path: '/integrations' },
  { text: 'Administration', icon: <SettingsIcon />, path: '/administration' },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { user, profile } = useAppSelector((state) => state.auth);

  // Auto-logout after inactivity (opaque-token session expiry)
  useSessionTimeout();

  // Workforce SSO usernames are employee numbers (e.g. "3..."), so prefer
  // the first ALPHABETIC letter of the full name for the avatar.
  const avatarInitial = (() => {
    const candidates = [
      user?.full_name,
      user?.first_name,
      [user?.first_name, user?.last_name].filter(Boolean).join(' '),
    ];
    for (const c of candidates) {
      const letter = c?.match(/[A-Za-z]/)?.[0];
      if (letter) return letter.toUpperCase();
    }
    return 'U';
  })();

  // Ensure profile data is available for the avatar/menu
  React.useEffect(() => {
    if (!profile) {
      dispatch(fetchUserProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleViewProfile = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleProfileMenuClose();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          EMISToolbox
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuClick(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'EMISToolbox'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationCenter />

            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {avatarInitial}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <ListItemIcon>
                <AccountIcon fontSize="small" />
              </ListItemIcon>
              <Box>
                <Typography variant="inherit" sx={{ fontWeight: 600 }}>
                  {user?.full_name || user?.username}
                </Typography>
                {profile?.designation && (
                  <Typography variant="caption" color="text.secondary">
                    {profile.designation}
                  </Typography>
                )}
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleViewProfile}>
              <ListItemIcon>
                <AccountIcon fontSize="small" />
              </ListItemIcon>
              View Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;